import type { OrderFragment } from '#graphql-operations'
import type { ComputedRef } from 'vue'
import type { GiftProgressEntry, PendingGift } from '~/utils/giftCopy'
import { giftProgressIsComplete, giftProgressStep } from '~/utils/giftCopy'

/**
 * Gift with order on the active order: how far the cart is from each rule's
 * next gift, and the gifts still waiting to be chosen.
 *
 * The backend does the arithmetic. `Order.giftProgress` has one entry per
 * eligible rule — threshold, current, remaining to the NEXT unit (0 once
 * capped), entitled/granted/pending units — already sorted: rules not yet
 * reached by progress ratio, closest first, then the reached ones. Money is in
 * minor units of the entry's currency, quantities in units. `pendingGifts`
 * lists the rules with units the customer still has to choose; checkout stays
 * blocked while it is non-empty.
 *
 * - `entries` — the progress list the cart may show. Empty for an empty cart
 *   ("add X more" would be an invitation to nothing) and for a
 *   digital-coupon-only cart (the backend never gives such an order a gift but
 *   may still report progress, so a bar would promise one it cannot deliver —
 *   the same guard the free-shipping bar applies). A HIDDEN rule is a
 *   surprise: it is left out until it has actually given something (a granted
 *   or pending unit), so the cart never advertises it in advance. A rule
 *   unlocked by a personal prize code (`requiresCode`) is shown whatever its
 *   style: it is on the order only because the customer applied their code, so
 *   "spend X more" is the answer they need.
 * - `primary` — the one bar worth showing first: the first entry that still
 *   has something to earn (not capped, and not a non-repeating rule already
 *   reached), i.e. the closest next gift; failing that, the first reached
 *   entry ("Free gift unlocked"). Null when there is nothing to show.
 * - `others` — every other entry, in the backend's order.
 * - `pendingGifts` / `totalPending` — the claims, and the number of gift units
 *   they add up to (a repeating rule can have several).
 *
 * Reads the order the way `useShippingEstimate` does: the legacy `$activeOrder`
 * ref first, the Colada store as the fallback for the window before the
 * legacy plugin's own fetch has resolved. Gift eligibility depends on the
 * delivery country (a rule may carry a country allowlist), and checkout's
 * address writes land only in the legacy cache while every cart mutation syncs
 * Colada -> legacy right after it runs — so legacy is the one copy that is
 * fresh in both contexts; store-first would keep showing the progress worked
 * out for the previous country after the customer changes it on checkout.
 */
export function useGiftProgress(): {
  order: ComputedRef<OrderFragment | null | undefined>
  entries: ComputedRef<GiftProgressEntry[]>
  primary: ComputedRef<GiftProgressEntry | null>
  others: ComputedRef<GiftProgressEntry[]>
  pendingGifts: ComputedRef<PendingGift[]>
  totalPending: ComputedRef<number>
} {
  const activeOrderStore = useActiveOrderStore()
  const { activeOrder: legacyOrder } = useActiveOrder()
  const { isDigitalOnlyOrder } = useDigitalCouponOrder()

  // The legacy ref is typed as always present, but it is null until its own
  // fetch resolves and undefined before that — say so, or `order` would claim
  // an order that is not there yet.
  const order = computed<OrderFragment | null | undefined>(() => legacyOrder.value ?? activeOrderStore.activeOrder.data)

  const itemCount = computed(() => order.value?.totalQuantity ?? 0)

  const entries = computed<GiftProgressEntry[]>(() => {
    if (itemCount.value <= 0 || isDigitalOnlyOrder.value)
      return []
    return (order.value?.giftProgress ?? []).filter(entry =>
      entry.promoStyle !== 'HIDDEN' || entry.requiresCode || entry.granted > 0 || entry.pending > 0)
  })

  // The closest next gift: among the entries with something left to earn, the
  // one furthest along its current step. The backend's order is not that —
  // it lists every rule not yet reached before any reached one, so a
  // repeating rule 90% of the way to its next unit would lose to a rule at 8%.
  // Ties keep the backend's order.
  const primary = computed<GiftProgressEntry | null>(() => {
    const open = entries.value.filter(entry => !giftProgressIsComplete(entry))
    const closest = open.reduce<GiftProgressEntry | null>((best, entry) =>
      !best || giftProgressStep(entry).percent > giftProgressStep(best).percent ? entry : best, null)
    return closest
      ?? entries.value.find(entry => entry.unlocked)
      ?? entries.value[0]
      ?? null
  })

  const others = computed<GiftProgressEntry[]>(() => {
    const first = primary.value
    return first ? entries.value.filter(entry => entry !== first) : entries.value
  })

  const pendingGifts = computed<PendingGift[]>(() => order.value?.pendingGifts ?? [])
  const totalPending = computed(() => pendingGifts.value.reduce((sum, gift) => sum + Math.max(0, gift.remaining), 0))

  return { order, entries, primary, others, pendingGifts, totalPending }
}
